import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Batches from './pages/Batches';
import BatchDetail from './pages/BatchDetail';
import Compliance from './pages/Compliance';
import VerifyBatch from './pages/VerifyBatch';
import CompareBatches from './pages/CompareBatches';
import OptimarDashboard from './pages/optimar/OptimarDashboard';
import OptimarBatchDetail from './pages/optimar/OptimarBatchDetail';
import OptimarCustomerView from './pages/optimar/OptimarCustomerView';
import OptimarDataMapping from './pages/optimar/OptimarDataMapping';
import OptimarComplianceReport from './pages/optimar/OptimarComplianceReport';
import DashboardLayout from './layouts/DashboardLayout';
import { Toaster } from './components/ui/sonner';
import './App.css';

function App() {
  // Single portal login. State lives in sessionStorage so closing the
  // browser/tab automatically re-locks the portal.
  const readUser = () => {
    try {
      const u = sessionStorage.getItem('user');
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState(readUser);
  const [token, setToken] = useState(() => sessionStorage.getItem('token') || null);

  useEffect(() => {
    // Keep state in sync if another tab logs in/out.
    const onStorage = () => {
      setUser(readUser());
      setToken(sessionStorage.getItem('token') || null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    sessionStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.setItem('token', userToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    // Also clear legacy localStorage keys from previous builds.
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.removeItem('optimar-demo-access');
    } catch { /* ignore */ }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <Dashboard user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/batches"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <Batches user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/batches/:id"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <BatchDetail user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/compliance"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <Compliance user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/compare"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <CompareBatches user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/verify/:id" element={<VerifyBatch />} />
          <Route
            path="/optimar"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <OptimarDashboard user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/optimar/batch"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <OptimarBatchDetail user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/optimar/customer"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <OptimarCustomerView user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/optimar/data-mapping"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <OptimarDataMapping user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/optimar/report"
            element={
              user ? (
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <OptimarComplianceReport user={user} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
